/* Copy da página de detalhe do publisher (ADR-0811): abas, badges de status e a barra de
   governança (verificar/suspender/banir + confirmação com motivo) e os toasts resultantes.
   `PublisherActions` (botões por linha) e o verbo de cada ação (`ACTION_VERB`) vivem em
   `components/publishers/` — fora do escopo desta migração; os toasts/diálogo que citam o
   verbo herdam o pt-BR de lá até aquela frente migrar (ver relatório da task). `statusLabel`
   já aceita override de rótulos (ADR-0471/0476) — usamos `statusLabels` daqui em vez do
   default pt-BR do próprio helper. */
import type { PublisherStatusLabels } from '../../components/publishers/publisher-status-map.js';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface PublisherDetailContent {
  readonly breadcrumbAriaLabel: string;
  readonly notFoundMessage: string;
  readonly statusLabels: PublisherStatusLabels;
  readonly domainVerifiedBadge: string;
  readonly tabs: {
    readonly overview: string;
    readonly keys: string;
    readonly moderation: string;
    readonly activity: string;
  };
  readonly fields: {
    readonly createdAt: string;
    readonly verifiedAt: string;
  };
  readonly publisherIdLabel: string;
  readonly moderationQueueLink: string;
  readonly activityLoadError: string;
  readonly loadMore: string;
  readonly verifySuccess: (signedKeyCount: number) => string;
  readonly verifyError: string;
  readonly actionAppliedSuccess: (verb: string) => string;
  readonly actionError: (verb: string) => string;
  readonly confirmActionTitle: string;
  readonly confirmActionDescription: (verb: string, publisherName: string) => string;
  readonly reasonHint: string;
  readonly reasonLabel: string;
  readonly reasonPlaceholder: string;
  readonly cancel: string;
  readonly confirm: string;
}

const PUBLISHER_DETAIL_CONTENT: Bilingual<PublisherDetailContent> = {
  'pt-BR': {
    breadcrumbAriaLabel: 'Trilha',
    notFoundMessage: 'Publisher não encontrado ou indisponível.',
    statusLabels: {
      verified: 'Verificado',
      banned: 'Banido',
      suspended: 'Suspenso',
      pending: 'Pendente',
    },
    domainVerifiedBadge: 'Domínio verificado',
    tabs: {
      overview: 'Visão geral',
      keys: 'Chaves',
      moderation: 'Moderação',
      activity: 'Atividade',
    },
    fields: { createdAt: 'Criado em', verifiedAt: 'Verificado em' },
    publisherIdLabel: 'ID do publisher',
    moderationQueueLink: 'Ver fila de moderação',
    activityLoadError: 'Falha ao carregar a atividade do publisher.',
    loadMore: 'Carregar mais',
    verifySuccess: (signedKeyCount) =>
      `Publisher verificado (${signedKeyCount} chave(s) assinada(s)).`,
    verifyError: 'Falha ao verificar o publisher.',
    actionAppliedSuccess: (verb) => `Publisher: ${verb} aplicado.`,
    actionError: (verb) => `Falha ao ${verb} o publisher.`,
    confirmActionTitle: 'Confirmar ação',
    confirmActionDescription: (verb, publisherName) => `${verb} “${publisherName}”.`,
    reasonHint: 'Informe um motivo (fica na trilha de auditoria).',
    reasonLabel: 'Motivo',
    reasonPlaceholder: 'Ex.: denúncia de abuso confirmada',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
  },
  'en-US': {
    breadcrumbAriaLabel: 'Breadcrumb',
    notFoundMessage: 'Publisher not found or unavailable.',
    statusLabels: {
      verified: 'Verified',
      banned: 'Banned',
      suspended: 'Suspended',
      pending: 'Pending',
    },
    domainVerifiedBadge: 'Domain verified',
    tabs: { overview: 'Overview', keys: 'Keys', moderation: 'Moderation', activity: 'Activity' },
    fields: { createdAt: 'Created at', verifiedAt: 'Verified at' },
    publisherIdLabel: 'Publisher ID',
    moderationQueueLink: 'View moderation queue',
    activityLoadError: 'Failed to load the publisher activity.',
    loadMore: 'Load more',
    verifySuccess: (signedKeyCount) => `Publisher verified (${signedKeyCount} key(s) signed).`,
    verifyError: 'Failed to verify the publisher.',
    actionAppliedSuccess: (verb) => `Publisher: ${verb} applied.`,
    actionError: (verb) => `Failed to ${verb} the publisher.`,
    confirmActionTitle: 'Confirm action',
    confirmActionDescription: (verb, publisherName) => `${verb} "${publisherName}".`,
    reasonHint: 'Provide a reason (it goes into the audit trail).',
    reasonLabel: 'Reason',
    reasonPlaceholder: 'e.g., confirmed abuse report',
    cancel: 'Cancel',
    confirm: 'Confirm',
  },
};

export const usePublisherDetailContent = (): PublisherDetailContent =>
  useBilingual(PUBLISHER_DETAIL_CONTENT);
